
import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { api } from "../../App";
import { decryptResponse } from "../CryptoUtil";

const ROWS_PER_PAGE = 10;

const Asm_management = () => {
  const [asmData, setAsmData] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Fetch ASM records from getasm.php
  const fetchAsmData = useCallback(async () => {
    try {
      setLoading(true);
      setError("");

      // Reuse the token saved by your existing login flow.
      // Update this key if your login stores the token elsewhere.
      const session = JSON.parse(
        localStorage.getItem("userSession") || "{}"
      );

      const token = session?.token;

      if (!token) {
        throw new Error(
          "Login token not found. Please check your existing login session."
        );
      }

      const response = await axios.get(`${api}getasm.php`, {
        withCredentials: true,
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
        timeout: 15000,
      });

      let result = response.data;

      // Decrypt only when the API returns an encrypted payload.
      if (result?.encrypted) {
        const encrypted = result.encrypted;

        if (!encrypted.payload || !encrypted.transactionId) {
          throw new Error("Invalid encrypted API response.");
        }

        result = decryptResponse(
          encrypted.payload,
          encrypted.transactionId
        );
      }

      if (typeof result === "string") {
        result = JSON.parse(result);
      }

      if (result?.success === false) {
        throw new Error(result.message || "Unable to fetch ASM records.");
      }

      const records = Array.isArray(result?.data)
        ? result.data
        : Array.isArray(result?.asms)
        ? result.asms
        : null;

      if (!records) {
        console.error("Unexpected API response:", result);
        throw new Error("ASM records were not found in the API response.");
      }

      setAsmData(records);
      setCurrentPage(1);
    } catch (err) {
      console.error("ASM fetch error:", err);
      console.error("ASM response:", err.response?.data);

      setAsmData([]);
      setError(
        err.response?.data?.message ||
          err.message ||
          "Unable to fetch ASM records."
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAsmData();
  }, [fetchAsmData]);

  // Search only the selected fields.
  const filteredData = useMemo(() => {
    const search = searchTerm.trim().toLowerCase();

    if (!search) {
      return asmData;
    }

    return asmData.filter((asm) =>
      [
        asm.id,
        asm.zsm_id,
        asm.admin_id,
        asm.name,
        asm.phone,
        asm.state,
        asm.working_area,
        asm.status,
      ].some((value) => {
        if (value === null || value === undefined) {
          return false;
        }

        const text = Array.isArray(value)
          ? value.join(", ")
          : String(value);

        return text.toLowerCase().includes(search);
      })
    );
  }, [asmData, searchTerm]);

  const totalPages = Math.max(
    1,
    Math.ceil(filteredData.length / ROWS_PER_PAGE)
  );

  const pageData = filteredData.slice(
    (currentPage - 1) * ROWS_PER_PAGE,
    currentPage * ROWS_PER_PAGE
  );

  // Display working states one below another.
  const getWorkingStates = (value) => {
    if (!value) {
      return [];
    }

    if (Array.isArray(value)) {
      return value.map(String).map((state) => state.trim()).filter(Boolean);
    }

    if (typeof value === "string") {
      try {
        const parsed = JSON.parse(value);

        if (Array.isArray(parsed)) {
          return parsed
            .map(String)
            .map((state) => state.trim())
            .filter(Boolean);
        }
      } catch {
        // The database value may be comma-separated.
      }

      return value
        .split(",")
        .map((state) => state.trim())
        .filter(Boolean);
    }

    return [];
  };

  const displayValue = (value) => {
    if (value === null || value === undefined || value === "") {
      return "—";
    }

    return String(value);
  };

  const handleSearch = (value) => {
    setSearchTerm(value);
    setCurrentPage(1);
  };

  // UI placeholder: connect your existing update endpoint to save edits.
  const handleEdit = (asm) => {
    console.log("Selected ASM for editing:", asm);
    alert("ASM edit API is needed to save changes.");
  };

  // UI placeholder: getasm.php only supports GET and cannot update status.
  const handleStatus = (asm) => {
    console.log("Selected ASM for status change:", asm);
    alert("ASM status update API is needed to save changes.");
  };

  return (
    <div className="container-fluid py-3">
      <div className="d-flex justify-content-between align-items-center flex-wrap gap-3 mb-4">
        <div>
          <h2 className="fw-bold mb-1">ASM Management</h2>
          <p className="text-muted mb-0">
            View and manage Area Sales Managers.
          </p>
        </div>

        <button
          type="button"
          className="btn btn-outline-secondary"
          onClick={fetchAsmData}
          disabled={loading}
        >
          {loading ? "Loading..." : "Refresh"}
        </button>
      </div>

      {error && (
        <div
          className="alert alert-danger d-flex justify-content-between align-items-center flex-wrap gap-2"
          role="alert"
        >
          <span>{error}</span>

          <button
            type="button"
            className="btn btn-sm btn-outline-danger"
            onClick={fetchAsmData}
            disabled={loading}
          >
            Retry
          </button>
        </div>
      )}

      <div className="card border-0 shadow-sm rounded-4 mb-4">
        <div className="card-body p-4">
          <label htmlFor="asmSearch" className="form-label fw-semibold">
            Search ASM
          </label>

          <input
            id="asmSearch"
            type="text"
            className="form-control"
            placeholder="Search by ASM ID, ZSM ID, Admin ID, name, phone or state"
            value={searchTerm}
            onChange={(event) => handleSearch(event.target.value)}
          />
        </div>
      </div>

      <div className="card border-0 shadow-sm rounded-4">
        <div className="card-body p-4">
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
            <h5 className="fw-bold mb-0">ASM List</h5>

            <span className="text-muted">
              Total entries: {filteredData.length}
            </span>
          </div>

          <div className="table-responsive">
            <table className="table table-bordered table-hover align-middle mb-0">
              <thead className="table-light">
                <tr>
                  <th>S.No.</th>
                  <th>ASM ID</th>
                  <th>ZSM ID</th>
                  <th>Admin ID</th>
                  <th>Name</th>
                  <th>Phone</th>
                  <th>State</th>
                  <th>Working area</th>
                  <th>Status</th>
                  <th style={{ minWidth: "180px" }}>Action</th>
                </tr>
              </thead>

              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="10" className="text-center py-5">
                      Loading ASM records...
                    </td>
                  </tr>
                ) : pageData.length > 0 ? (
                  pageData.map((asm, index) => {
                    const workingStates = getWorkingStates(
                      asm.working_state
                    );

                    return (
                      <tr key={asm.id ?? `${asm.zsm_id}-${index}`}>
                        <td>
                          {(currentPage - 1) * ROWS_PER_PAGE + index + 1}
                        </td>

                        <td className="fw-semibold">
                          {displayValue(asm.id)}
                        </td>

                        <td>{displayValue(asm.zsm_id)}</td>
                        <td>{displayValue(asm.admin_id)}</td>
                        <td>{displayValue(asm.name)}</td>
                        <td>{displayValue(asm.phone)}</td>
                        <td>{displayValue(asm.state)}</td>

                        <td>
                          {workingStates.length > 0 ? (
                            <div>
                              {workingStates.map((state, stateIndex) => (
                                <div key={`${state}-${stateIndex}`}>
                                  {state}
                                </div>
                              ))}
                            </div>
                          ) : (
                            "—"
                          )}
                        </td>

                        <td>
                          <span
                            className={`badge rounded-pill ${
                              String(asm.status).toLowerCase() === "active"
                                ? "bg-success"
                                : "bg-danger"
                            }`}
                          >
                            {displayValue(asm.status)}
                          </span>
                        </td>

                        <td>
                          <div className="d-flex align-items-center gap-2 flex-nowrap">
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-primary"
                              onClick={() => handleEdit(asm)}
                            >
                              Edit
                            </button>

                            <button
                              type="button"
                              className={`btn btn-sm flex-shrink-0 ${
                                String(asm.status).toLowerCase() === "active"
                                  ? "btn-outline-danger"
                                  : "btn-outline-success"
                              }`}
                              onClick={() => handleStatus(asm)}
                            >
                              {String(asm.status).toLowerCase() === "active"
                                ? "Inactive"
                                : "Active"}
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="10" className="text-center py-5">
                      <h6 className="fw-semibold mb-1">No records found</h6>
                      <span className="text-muted">
                        Try another search term.
                      </span>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="d-flex justify-content-between align-items-center flex-wrap gap-3 mt-3">
            <span className="text-muted">
              Showing{" "}
              {filteredData.length === 0
                ? 0
                : (currentPage - 1) * ROWS_PER_PAGE + 1}
              {"–"}
              {Math.min(
                currentPage * ROWS_PER_PAGE,
                filteredData.length
              )}{" "}
              of {filteredData.length} entries
            </span>

            <div className="btn-group">
              <button
                type="button"
                className="btn btn-outline-secondary"
                disabled={currentPage <= 1}
                onClick={() =>
                  setCurrentPage((page) => Math.max(1, page - 1))
                }
              >
                Previous
              </button>

              <button
                type="button"
                className="btn btn-secondary"
                disabled
              >
                {currentPage} / {totalPages}
              </button>

              <button
                type="button"
                className="btn btn-outline-secondary"
                disabled={currentPage >= totalPages}
                onClick={() =>
                  setCurrentPage((page) => Math.min(totalPages, page + 1))
                }
              >
                Next
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Asm_management;
